// components/ui/DeleteConfirmDialog.tsx
// -----------------------------------------------------------------------------
// Reusable delete confirmation dialog with consistent styling.
// -----------------------------------------------------------------------------

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "./AlertDialog";

interface DeleteConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Title of the dialog (e.g., "Delete Post", "Delete Comment") */
  title: string;
  /** Description text explaining the action */
  description?: string;
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
  /** Text for the delete button (default: "Delete") */
  deleteLabel?: string;
  /** Text shown while deleting (default: "Deleting...") */
  deletingLabel?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelLabel?: string;
}

/**
 * A standardized delete confirmation dialog.
 *
 * @example
 * <DeleteConfirmDialog
 *   open={showDeleteDialog}
 *   onOpenChange={setShowDeleteDialog}
 *   title="Delete Comment"
 *   description="Are you sure you want to delete this comment? This action cannot be undone."
 *   isDeleting={isDeleting}
 *   onConfirm={handleDelete}
 * />
 */
export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  description = "Are you sure? This action cannot be undone.",
  isDeleting = false,
  onConfirm,
  deleteLabel = "Delete",
  deletingLabel = "Deleting...",
  cancelLabel = "Cancel",
}: DeleteConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-3 pt-4">
          <AlertDialogCancel
            className="w-full h-auto py-2 rounded-full"
            disabled={isDeleting}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className="w-full h-auto py-2 rounded-full bg-red hover:bg-red/90 text-off-white"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? deletingLabel : deleteLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
